import React from 'react';
import { AxisConfig, ScaleConfig, ChartDimensions } from '../../types';
import { formatValue } from '../../utils/scales';

interface AxisProps {
  type: 'x' | 'y';
  config: AxisConfig;
  scale: ScaleConfig;
  dimensions: ChartDimensions;
  labels?: string[]; // For category axis
  orientation?: 'vertical' | 'horizontal';
  isMobile?: boolean;
}

/**
 * Axis component - Renders axis line, ticks, and labels
 * Responsive font sizing for mobile
 */
export const Axis: React.FC<AxisProps> = ({
  type,
  config,
  scale,
  dimensions,
  labels,
  orientation = 'vertical',
  isMobile = false,
}) => {
  if (!config.display) return null;

  const { chartWidth, chartHeight } = dimensions;
  const isHorizontal = orientation === 'horizontal';
  
  // Responsive font sizes
  const tickFontSize = isMobile 
    ? Math.max(9, (config.ticks?.fontSize || 11) - 2)
    : config.ticks?.fontSize || 11;
  
  const titleFontSize = isMobile
    ? Math.max(10, (config.title?.fontSize || 12) - 2)
    : config.title?.fontSize || 12;

  // For bar charts, X is always categories, Y is always values
  // Unless horizontal, then it's reversed
  const isValueAxis = (type === 'y' && !isHorizontal) || (type === 'x' && isHorizontal);
  const isCategoryAxis = !isValueAxis;

  // Render value axis (numeric ticks)
  if (isValueAxis) {
    const ticks = scale.ticks;
    const { min, max } = scale;
    const range = max - min;

    // Reduce number of ticks on mobile
    const visibleTicks = isMobile && ticks.length > 6
      ? ticks.filter((_, i) => i % 2 === 0 || i === ticks.length - 1)
      : ticks;

    return (
      <g className={`axis axis-${type}`} aria-hidden="true">
        {/* Grid lines */}
        {config.grid?.display && visibleTicks.map((tick, i) => {
          const position = ((tick - min) / range) * (type === 'y' ? chartHeight : chartWidth);
          const y = type === 'y' ? chartHeight - position : 0;
          const x = type === 'x' ? position : 0;
          
          return (
            <line
              key={`grid-${i}`}
              x1={type === 'y' ? 0 : x}
              y1={type === 'y' ? y : 0}
              x2={type === 'y' ? chartWidth : x}
              y2={type === 'y' ? y : chartHeight}
              stroke={config.grid?.color || 'rgba(0,0,0,0.1)'}
              strokeWidth={config.grid?.lineWidth || 1}
              strokeDasharray={config.grid?.dashArray}
            />
          );
        })}

        {/* Axis line */}
        <line
          x1={type === 'y' ? 0 : 0}
          y1={type === 'y' ? 0 : chartHeight}
          x2={type === 'y' ? 0 : chartWidth}
          y2={type === 'y' ? chartHeight : chartHeight}
          stroke={config.grid?.color || 'rgba(0,0,0,0.1)'}
          strokeWidth={1}
        />

        {/* Tick labels */}
        {config.ticks?.display && visibleTicks.map((tick, i) => {
          const position = ((tick - min) / range) * (type === 'y' ? chartHeight : chartWidth);
          const y = type === 'y' ? chartHeight - position : chartHeight;
          const x = type === 'x' ? position : 0;
          
          let label: string;
          if (config.ticks?.callback) {
            label = config.ticks.callback(tick, i);
          } else {
            // Smart number formatting
            const absValue = Math.abs(tick);
            if (absValue >= 1000000) {
              label = (tick / 1000000).toFixed(1) + 'M';
            } else if (absValue >= 1000) {
              label = (tick / 1000).toFixed(absValue >= 10000 ? 0 : 1) + 'K';
            } else {
              label = formatValue(tick);
            }
          }

          return (
            <text
              key={`tick-${i}`}
              x={type === 'y' ? -(config.ticks?.padding || 8) : x}
              y={type === 'y' ? y : y + (config.ticks?.padding || 8) + tickFontSize}
              textAnchor={type === 'y' ? 'end' : 'middle'}
              dominantBaseline={type === 'y' ? 'middle' : 'hanging'}
              fill={config.ticks?.color || '#666'}
              fontSize={tickFontSize}
              fontFamily={config.ticks?.fontFamily}
            >
              {label}
            </text>
          );
        })}

        {/* Axis title */}
        {config.title?.display && config.title.text && (
          <text
            x={type === 'y' ? -(chartHeight / 2) : chartWidth / 2}
            y={type === 'y' ? -(config.title.padding || 35) : chartHeight + (config.title.padding || 35)}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={config.title.color || '#666'}
            fontSize={titleFontSize}
            fontWeight={config.title.fontWeight || 'normal'}
            fontFamily={config.title.fontFamily}
            transform={type === 'y' ? `rotate(-90, ${-(config.title.padding || 35)}, ${chartHeight / 2})` : undefined}
          >
            {config.title.text}
          </text>
        )}
      </g>
    );
  }

  // Render category axis (labels)
  if (isCategoryAxis && labels) {
    const bandWidth = (type === 'x' ? chartWidth : chartHeight) / labels.length;
    
    // Rotate labels on mobile if they're too long or too many
    const shouldRotateLabels = isMobile && (
      labels.length > 5 || 
      labels.some(l => l.length > 6)
    );

    // Skip some labels on mobile if too crowded
    const maxLabels = isMobile ? 7 : 12;
    const skipInterval = labels.length > maxLabels ? Math.ceil(labels.length / maxLabels) : 1;

    return (
      <g className={`axis axis-${type}`} aria-hidden="true">
        {/* Axis line */}
        <line
          x1={type === 'x' ? 0 : 0}
          y1={type === 'x' ? chartHeight : 0}
          x2={type === 'x' ? chartWidth : 0}
          y2={type === 'x' ? chartHeight : chartHeight}
          stroke={config.grid?.color || 'rgba(0,0,0,0.1)'}
          strokeWidth={1}
        />

        {/* Category labels */}
        {config.ticks?.display && labels.map((label, i) => {
          // Skip some labels if too crowded
          if (skipInterval > 1 && i % skipInterval !== 0 && i !== labels.length - 1) {
            return null;
          }
          
          const position = (i + 0.5) * bandWidth;
          
          // Truncate long labels on mobile
          const maxLabelLength = isMobile ? 8 : 15;
          const displayLabel = label.length > maxLabelLength 
            ? label.substring(0, maxLabelLength - 1) + '…' 
            : label;
          
          if (type === 'x') {
            return (
              <text
                key={`label-${i}`}
                x={position}
                y={chartHeight + (config.ticks?.padding || 8) + tickFontSize}
                textAnchor={shouldRotateLabels ? 'end' : 'middle'}
                dominantBaseline="hanging"
                fill={config.ticks?.color || '#666'}
                fontSize={tickFontSize}
                fontFamily={config.ticks?.fontFamily}
                transform={shouldRotateLabels ? `rotate(-45, ${position}, ${chartHeight + (config.ticks?.padding || 8) + tickFontSize})` : undefined}
              >
                {displayLabel}
              </text>
            );
          } else {
            return (
              <text
                key={`label-${i}`}
                x={-(config.ticks?.padding || 8)}
                y={position}
                textAnchor="end"
                dominantBaseline="middle"
                fill={config.ticks?.color || '#666'}
                fontSize={tickFontSize}
                fontFamily={config.ticks?.fontFamily}
              >
                {displayLabel}
              </text>
            );
          }
        })}

        {/* Axis title */}
        {config.title?.display && config.title.text && (
          <text
            x={type === 'x' ? chartWidth / 2 : -(chartHeight / 2)}
            y={type === 'x' ? chartHeight + (config.title.padding || (shouldRotateLabels ? 50 : 35)) : -(config.title.padding || 35)}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={config.title.color || '#666'}
            fontSize={titleFontSize}
            fontWeight={config.title.fontWeight || 'normal'}
            fontFamily={config.title.fontFamily}
            transform={type === 'y' ? `rotate(-90, ${-(config.title.padding || 35)}, ${chartHeight / 2})` : undefined}
          >
            {config.title.text}
          </text>
        )}
      </g>
    );
  }

  return null;
};

export default Axis;
